// PLAN-permalink-cockpit-convergence Phase C: permalink JSON 取得
//
// /v/:id を same-origin で fetch する (apiFetch は使わない)。
// 理由: apiFetch は config.facadeOrigin で URL を組むため Pages Function プロキシと
// kdb_session cookie 経路を bypass してしまう。permalink は first-party cookie 経路を維持する。
import { storage } from '../auth/storage'
import type { PvwDecisionPayload } from '../types'

// 生 R2 JSON (Entry / Article 共通の superset)。PermalinkView が DetailItem に normalize する。
export interface PermalinkRawEntry {
  id: string
  title: string
  content?: string
  abstract?: { one_line?: string; key_points?: string[] }
  origin: string
  subjectivity?: string
  visibility?: string
  source?: { tool?: string; medium?: string; project?: string | null }
  source_project?: string | null
  source_actor?: string | null
  source_context?: string | null
  declared_domains?: string[]
  previous_version?: string | null
  source_article_ids?: string[]
  created_at: string
  article_meta?: {
    theme?: string
    version_label?: string | null
    source_entry_ids?: string[]
    confidence?: string
    applicability?: string
    confidence_basis?: string | null
    lifecycle_stage?: string
    stale_by?: string | null
    review_by?: string | null
  }
  decision?: PvwDecisionPayload
}

export interface PermalinkRelated {
  titleMap: Record<string, { title: string; origin: string }>
  referencedBy: { id: string; title: string; origin: string }[]
  versionChain: { id: string; title: string; created_at: string; version_label: string | null }[]
  domains: { domain: string; provenance: string; confidence?: number | null }[]
  signals?: { view_count: number; sourced_count: number; last_accessed_at: string | null }
}

export interface PermalinkResponse {
  requested_id: string
  resolved_id: string
  // 旧版 (resolved_id !== requested_id) のときは entry/related が省略される
  entry?: PermalinkRawEntry
  related?: PermalinkRelated
}

export class PermalinkNotFoundError extends Error {
  constructor(public id: string) {
    super(`permalink not found: ${id}`)
  }
}

/**
 * /v/:id を JSON で取得する。旧版なら resolved_id で 1 度だけ再 fetch する。
 * same-origin fetch + credentials:'include' で kdb_session cookie を送る。
 * Cockpit ログイン済みなら Bearer も付ける (Facade は Bearer → Cookie の順に判定)。
 */
export async function fetchPermalinkJson(id: string): Promise<PermalinkResponse> {
  const fetchOnce = async (targetId: string): Promise<PermalinkResponse> => {
    const headers: Record<string, string> = { Accept: 'application/json' }
    const token = storage.getAccessToken()
    if (token) headers['Authorization'] = `Bearer ${token}`
    const res = await fetch(`/v/${encodeURIComponent(targetId)}`, {
      headers,
      credentials: 'include',
    })
    if (res.status === 404) throw new PermalinkNotFoundError(targetId)
    if (!res.ok) throw new Error(`permalink fetch failed: ${res.status}`)
    return (await res.json()) as PermalinkResponse
  }

  const first = await fetchOnce(id)
  if (first.resolved_id && first.resolved_id !== first.requested_id && !first.entry) {
    return fetchOnce(first.resolved_id)
  }
  return first
}
